import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Calendar } from 'lucide-react';

const API = `${window.location.origin}/api`;

/**
 * 30-day growth chart (CSS bar chart — no chart lib).
 * - Bars are normalized to the max bottle count in the window.
 * - Today is highlighted with gold accent.
 * - Hover tooltip shows date + bottles + revenue.
 */
export default function GrowthChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  useEffect(() => {
    axios.get(`${API}/ambassador/me/growth?days=30`, { headers: headers() })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const maxBottles = Math.max(1, ...data.series.map(d => d.bottles));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <section className="bg-[#0F0F0F] border border-white/10 p-5 sm:p-6 mb-8" data-testid="growth-chart">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Last 30 Days
          </p>
          <h3 className="text-xl text-white font-medium mt-1 font-sans">Your sales momentum</h3>
        </div>
        <div className="flex gap-5 text-right">
          <div>
            <p className="text-2xl text-white font-medium font-sans" data-testid="growth-total-bottles">{data.total_bottles}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans">bottles sold</p>
          </div>
          <div>
            <p className="text-2xl text-[#D4AF37] font-medium font-sans" data-testid="growth-total-revenue">₱{Number(data.total_revenue).toLocaleString()}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans">revenue</p>
          </div>
        </div>
      </div>

      {/* Bar chart */}
      <div className="relative h-32 flex items-end gap-[2px]" data-testid="growth-bars">
        {data.series.map((d) => {
          const h = (d.bottles / maxBottles) * 100;
          const isToday = d.date === today;
          const hasSales = d.bottles > 0;
          return (
            <button
              key={d.date}
              type="button"
              onMouseEnter={() => setHover(d)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(d)}
              onBlur={() => setHover(null)}
              className="flex-1 group flex flex-col justify-end h-full focus:outline-none"
              aria-label={`${d.date}: ${d.bottles} bottles`}
            >
              <div
                className={`w-full transition-all rounded-t-sm ${isToday ? 'bg-[#D4AF37]' : hasSales ? 'bg-[#1C7D3F] group-hover:bg-[#2E9B50]' : 'bg-white/10 group-hover:bg-white/20'}`}
                style={{ height: hasSales ? `${Math.max(h, 6)}%` : '4%' }}
              />
            </button>
          );
        })}
      </div>

      {/* X-axis label + tooltip */}
      <div className="flex items-center justify-between mt-3 text-[10px] uppercase tracking-[0.18em] text-white/40 font-sans">
        <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {data.series[0]?.date.slice(5)}</span>
        <span className="text-white/60" data-testid="growth-tooltip">
          {hover ? (
            <span>
              <span className="text-white">{new Date(hover.date).toLocaleDateString('en-PH', { dateStyle: 'medium' })}</span>
              {' · '}
              <span className="text-[#D4AF37]">{hover.bottles} bottles</span>
              {hover.revenue > 0 && <span className="text-white/60"> · ₱{Number(hover.revenue).toLocaleString()}</span>}
            </span>
          ) : (
            <span>Hover any bar for the daily breakdown</span>
          )}
        </span>
        <span>Today</span>
      </div>

      {data.best_day && data.best_day.bottles > 0 && (
        <p className="mt-3 text-xs text-white/50 font-sans">
          🔥 Your best day: <span className="text-[#D4AF37]">{new Date(data.best_day.date).toLocaleDateString('en-PH', { dateStyle: 'medium' })}</span> with <span className="text-white">{data.best_day.bottles} bottles</span>.
        </p>
      )}
    </section>
  );
}
